import React from 'react';
import { fmt, fvLump } from '../utils/format';
import { computeRetirementTargets } from '../utils/calculations';
import { IRS_IRA_LIMIT } from '../utils/constants';

// Read-only retirement / goal / debt projections. Lives inside the Strategy tab
// (formerly the standalone "Goals" tab). All inputs are edited in Setup — this
// panel only forecasts. Pass in the data the parent already loaded.

const TYPE_LABEL = {
  RETIREMENT_401K: '401k', IRA: 'IRA', BROKERAGE: 'Brokerage',
  COLLEGE: 'College fund', OTHER: 'Savings goal',
};

function Field({ label, children }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid var(--border)' }}>
      <span style={{ fontSize:13, color:'var(--text2)', flex:1, paddingRight:8 }}>{label}</span>
      {children}
    </div>
  );
}

function ReadVal({ value, prefix='$' }) {
  const text = prefix === '$' ? fmt(value || 0)
             : prefix === '%' ? `${value || 0}%`
             : `${value ?? ''}`;
  return <span style={{ fontSize:13, fontWeight:600 }}>{text}</span>;
}

function ResultRow({ label, value, highlight, color }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 10px', borderRadius:6, marginBottom:4, background: highlight?'var(--blue-bg)':'var(--bg)', border:`1px solid ${highlight?'var(--border2)':'var(--border)'}` }}>
      <span style={{ fontSize:13, color:'var(--text2)' }}>{label}</span>
      <span style={{ fontSize:14, fontWeight:600, color: color||(highlight?'var(--blue)':'var(--text)') }}>{value}</span>
    </div>
  );
}

function ProgressBar({ current, goal, color='var(--green)' }) {
  const pct = goal>0 ? Math.min(100, (current/goal)*100) : 0;
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:3 }}>
        <span style={{ color:'var(--text2)' }}>{fmt(current)}</span>
        <span style={{ fontWeight:600 }}>{pct.toFixed(1)}%</span>
        <span style={{ color:'var(--text3)' }}>goal: {fmt(goal)}</span>
      </div>
      <div className="progress-track" style={{ height:8 }}>
        <div className="progress-fill" style={{ width:`${pct}%`, background:color }}/>
      </div>
    </div>
  );
}

export default function ProjectionsPanel({ members = [], savings = [], debts = [], settings, incomes = [] }) {
  if (!settings) return null;

  const targets = computeRetirementTargets({
    members, savingsPlans: savings, debtPlans: debts, settings, incomes,
  });
  const st = targets.savingsTargets, dt = targets.debtTargets;
  const memberName = (id) => members.find(m => m.id === id)?.name;

  const retirementPlans = savings.filter(p => ['RETIREMENT_401K','IRA','BROKERAGE'].includes(p.type));
  const goalPlans       = savings.filter(p => ['COLLEGE','OTHER'].includes(p.type));
  const totalRetBal     = retirementPlans.reduce((s, p) => s + (p.balance || 0), 0);

  if (members.length === 0 && savings.length === 0 && debts.length === 0) {
    return (
      <div style={{ fontSize: 13, color: 'var(--text3)', padding: '8px 2px' }}>
        Add household members, savings plans, and debts in <strong>Setup</strong> — a forecast for
        each will appear here.
      </div>
    );
  }

  return (
    <>
      <div style={{ marginBottom:14, fontSize:12, color:'var(--text3)' }}>
        Projections only — balances, rates, dates and the retirement goal are set in <strong>Setup</strong>.
      </div>

      {/* SHARED RETIREMENT GOAL */}
      <div className="card" style={{ background:'var(--blue-bg)', border:'1px solid var(--border2)' }}>
        <div className="sec-label">Shared Retirement Goal &amp; Dates</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:16 }}>
          <Field label="Total household retirement goal">
            <ReadVal value={settings.sharedRetirementGoal} />
          </Field>
          {members.map(m => (
            <Field key={m.id} label={`${m.name} retirement date`}>
              <ReadVal prefix="" value={m.retirementDate || '— not set'} />
            </Field>
          ))}
        </div>
        <div style={{ marginTop:14 }}>
          <ProgressBar current={totalRetBal} goal={settings.sharedRetirementGoal || 0} color='var(--blue)' />
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10, marginTop:8 }}>
            <div className="metric-box"><div className="label">401k FV still needed</div><div className="value" style={{ fontSize:16 }}>{fmt(targets.k401kFVNeeded)}</div></div>
            <div className="metric-box"><div className="label">Combined savings/mo needed</div><div className="value" style={{ fontSize:16 }}>{fmt(targets.totalSavingsMo)}</div></div>
          </div>
        </div>
      </div>

      {/* Retirement savings plan calculators */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(330px,1fr))', gap:16, marginBottom:16 }}>
        {retirementPlans.map(p => {
          const t = st[p.id] || {};
          const iraMax = p.yearlyMax ?? IRS_IRA_LIMIT;
          return (
            <div className="card" key={p.id} style={{ margin:0 }}>
              <div className="sec-label">{p.name}</div>
              <div style={{ fontSize:11, color:'var(--text3)', marginBottom:8 }}>
                {TYPE_LABEL[p.type]}{p.ownerMemberId ? ` · ${memberName(p.ownerMemberId)}` : ' · shared'}
              </div>
              <Field label="Current balance">
                <ReadVal value={p.balance} />
              </Field>
              <Field label="Annual return rate">
                <ReadVal prefix="%" value={parseFloat(((p.annualRate||0)*100).toFixed(2))} />
              </Field>
              {p.type === 'IRA' && (
                <Field label="IRS yearly contribution max">
                  <ReadVal value={iraMax} />
                </Field>
              )}
              {p.type === 'BROKERAGE' && (
                <Field label="Share of retirement goal">
                  <ReadVal prefix="%" value={parseFloat((((p.targetGoalPct ?? (settings.iraGoalSharePct ?? 0.145)))*100).toFixed(1))} />
                </Field>
              )}
              <div style={{ marginTop:12 }}>
                <ResultRow label="Months to retirement" value={t.months ?? 0} />
                {(p.type === 'RETIREMENT_401K' || p.type === 'BROKERAGE') &&
                  <ResultRow label="Responsible portion (FV)" value={fmt(t.fvNeeded || 0)} />}
                <ResultRow label="Balance grows to" value={fmt(fvLump(p.balance||0, p.annualRate||0, t.months||0))} />
                <ResultRow label="Monthly contribution needed" value={fmt(t.monthlyTarget || 0)} highlight />
                {p.type === 'IRA' && (t.monthlyTarget||0) > iraMax/12 + 0.005 && (
                  <ResultRow label="⚠ Exceeds IRS limit" value={fmt(iraMax/12)+'/mo max'} color="var(--red)" />
                )}
                {p.type === 'BROKERAGE' && (
                  <ResultRow label="Total FV (w/ contributions)" value={fmt(t.fv || 0)} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Goal-based savings (college funds etc.) */}
      {goalPlans.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(330px,1fr))', gap:16, marginBottom:16 }}>
          {goalPlans.map(p => {
            const t = st[p.id] || {};
            const goalAmt = (p.targetAmount || 0) * (p.targetGoalPct ?? 1);
            return (
              <div className="card" key={p.id} style={{ margin:0 }}>
                <div className="sec-label">{p.name}</div>
                <Field label="Current balance">
                  <ReadVal value={p.balance} />
                </Field>
                <Field label="Total cost / target">
                  <ReadVal value={p.targetAmount ?? 0} />
                </Field>
                <Field label="Goal (% of total)">
                  <ReadVal prefix="%" value={parseFloat((((p.targetGoalPct ?? 1))*100).toFixed(0))} />
                </Field>
                <Field label="Annual return">
                  <ReadVal prefix="%" value={parseFloat(((p.annualRate||0)*100).toFixed(2))} />
                </Field>
                <Field label="Target date">
                  <ReadVal prefix="" value={p.targetDate || '— not set'} />
                </Field>
                <div style={{ marginTop:12 }}>
                  <ProgressBar current={p.balance || 0} goal={goalAmt} color="var(--amber)" />
                  <ResultRow label="Goal amount" value={fmt(goalAmt)} />
                  <ResultRow label="Months remaining" value={t.months ?? 0} />
                  <ResultRow label="Monthly needed" value={fmt(t.monthlyTarget || 0)} highlight />
                  <ResultRow label="Projected balance" value={fmt(t.fv || 0)} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Debt payoff calculators */}
      {debts.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(330px,1fr))', gap:16, marginBottom:16 }}>
          {debts.map(p => {
            const t = dt[p.id] || {};
            return (
              <div className="card" key={p.id} style={{ margin:0 }}>
                <div className="sec-label">{p.name} Payoff</div>
                <div style={{ fontSize:11, color:'var(--text3)', marginBottom:8 }}>
                  {p.type === 'MORTGAGE' ? 'Mortgage' : p.type === 'STUDENT_LOAN' ? 'Student loan' : 'Debt'}
                  {p.ownerMemberId ? ` · ${memberName(p.ownerMemberId)}` : ''}
                </div>
                <Field label="Principal remaining">
                  <ReadVal value={p.principal} />
                </Field>
                <Field label="Interest rate">
                  <ReadVal prefix="%" value={parseFloat(((p.annualRate||0)*100).toFixed(2))} />
                </Field>
                <Field label="Regular monthly payment">
                  <ReadVal value={p.baseMonthlyPmt} />
                </Field>
                {p.type === 'MORTGAGE' && (
                  <Field label="Escrow per month">
                    <ReadVal value={p.escrow ?? 0} />
                  </Field>
                )}
                <Field label="Payoff goal date">
                  <ReadVal prefix="" value={p.payoffGoalDate || '— not set'} />
                </Field>
                <div style={{ marginTop:12 }}>
                  {(t.months || 0) > 0
                    ? <ResultRow label="Months remaining" value={t.months} />
                    : <ResultRow label="Months remaining" value="— set date" color="var(--text3)" />}
                  <ResultRow label="Extra principal/month" value={fmt(t.extraGoal || 0)} highlight />
                  <ResultRow label="Total monthly payment" value={fmt(t.totalMo || 0)} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
